import React from 'react';
import { useTranslation } from 'react-i18next';
import CustomModal from '../../components-library/CustomModal';
import { MODAL_SIZES } from '../../services/exports/Constants';
import useScreenType from '../../hooks/utility/useScreenType';
import useBasket from '../../hooks/basket/useBasket';
import { CompleteBasketItem } from '../../providers/BasketProvider';
import OrderBasketItem from '../../components-library/basket/order-basket-item';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import CloseIcon from '../../assets/icons/actions/CloseIcon';
import useTheme from '../../hooks/ui/useTheme';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useAllergens from '../../hooks/menu/useAllergens';
import WarningIcon from '../../assets/logo/WarningIcon';
import CustomButton from '../../components-library/buttons/CustomButton';
import { ButtonTypes } from '../../services/exports/Interfaces';

type Props = {
  open: boolean;
  onClose: () => void;
};

const AllergensConfirmationModal = ({ open, onClose }: Props) => {
  const { t } = useTranslation(null, { keyPrefix: 'Modals:Allergens:AllergensConfirmationModal' });

  const { isDesktop } = useScreenType();
  const { items } = useBasket();
  const { data } = useOrderFlow();
  const theme = useTheme();
  const { toggleMenuItemModal } = useAppMethods();
  const { setIsAllergensOpened, setAllergensConfirmed } = useAllergens();

  const onItemClick = (item: CompleteBasketItem, index: number) => {
    setIsAllergensOpened(true);
    toggleMenuItemModal({
      openModal: true,
      product: item.entry,
      isFreeItem: item.is_free,
      lineItem: item,
      basketItemIndex: index,
    });
  };

  const onConfirm = () => {
    setAllergensConfirmed();
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      size={MODAL_SIZES.SM}
      fullScreen={!isDesktop}
      containerClassName="px-md py-lg"
    >
      <div className="mb-xl flex justify-between items-center">
        <header className="text-md font-semibold">{t('title')}</header>
        <button onClick={onClose} className="cursor-pointer">
          <CloseIcon className="h-4 w-4" color={theme?.color.brand.icon.color} />
        </button>
      </div>
      <div className="space-y-md">
        {items.map((item: CompleteBasketItem, index) => (
          <div key={`basket-item-${index}`}>
            <OrderBasketItem
              data={item}
              coupon_type={data.coupon_type}
              index={index}
              extra={
                <button className="text-success" onClick={() => onItemClick(item, index)}>
                  {t('buttons.view_product_allergens')}
                </button>
              }
            />
          </div>
        ))}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <div>
              <h4 className="flex items-center gap-xs font-semibold text-red-900 mb-2">
                <WarningIcon />
                {t('alert.title')}
              </h4>
              <p className="text-sm text-red-800 font-medium">{t('alert.description')}</p>
            </div>
          </div>
        </div>
        <CustomButton onClick={onConfirm} title={t('buttons.confirm')} />
        <CustomButton onClick={onClose} title={t('buttons.cancel')} buttonType={ButtonTypes.secondary} />
      </div>
    </CustomModal>
  );
};

export default AllergensConfirmationModal;
